import type { TFunction } from 'i18next';
import type { Form, FormSummary, Problem } from '@/api';
import { problemCopy } from '@/components/Problems/problemCopy';
import type { RiskStripItem } from '@/components/Ui/RiskStrip';
import type { FormSpec } from '@/lib/form-specs';

export interface RiskInput {
  form: Pick<Form, 'id' | 'allowed_origins' | 'check_specs'>;
  problems: Problem[] | null;
  summary: FormSummary | null;
  spec: FormSpec | null;
  hasMessages: boolean;
}

/** How many fields reject, mark as spam or silently discard the whole submission when invalid. */
export function failureRuleCounts(spec: FormSpec | null): {
  reject: number;
  spam: number;
  trash: number;
} {
  const counts = { reject: 0, spam: 0, trash: 0 };
  for (const field of Object.values(spec?.fields ?? {})) {
    const onFail = field.on_fail ?? 'pass';
    if (onFail !== 'pass') counts[onFail] += 1;
  }
  return counts;
}

/** "Rejects submission: 2 fields", in the editor's own short labels so the wording cannot drift. */
export function failureRuleSummary(t: TFunction, spec: FormSpec | null): string[] {
  const counts = failureRuleCounts(spec);
  return (['reject', 'spam', 'trash'] as const)
    .filter((rule) => counts[rule] > 0)
    .map((rule) =>
      t('form.fields.ruleCount', { count: counts[rule], rule: t(`fields.rule.${rule}`) })
    );
}

/**
 * Action and waiting problems of this form, the conditions that reject or discard
 * submissions by design (allowed origins, field rules), and this week's spam.
 */
export function riskItems(t: TFunction, locale: string, input: RiskInput): RiskStripItem[] {
  const { form, problems, summary, spec, hasMessages } = input;
  const ownAnchor = `/account/forms/${form.id}#`;
  const items: RiskStripItem[] = [];
  const active = (problems ?? []).filter((p) => !p.muted);
  const captchaRejected = active.some((p) => p.cause === 'captcha_rejections');

  for (const p of active) {
    if (p.group !== 'action' && p.group !== 'waiting') continue;
    if (p.cause === 'captcha_unconfirmed') {
      // Rejections already say the widget is missing.
      if (captchaRejected) continue;
      items.push({
        text: hasMessages
          ? problemCopy(t, p, locale).body
          : t('problems.causes.captcha_unconfirmed.bodyNew'),
        link: { label: t('form.site.title'), to: '#site' },
        waiting: true,
      });
      continue;
    }
    const copy = problemCopy(t, p, locale);
    const link = copy.fix
      ? {
          label: copy.fix.label,
          to: copy.fix.to.startsWith(ownAnchor)
            ? `#${copy.fix.to.slice(ownAnchor.length)}`
            : copy.fix.to,
        }
      : undefined;
    items.push({
      text: copy.title ? (
        <>
          <strong>{copy.title}</strong> {copy.body}
        </>
      ) : (
        copy.body
      ),
      link,
      waiting: p.group === 'waiting',
    });
  }

  const origins = form.allowed_origins ?? [];
  if (origins.length > 0) {
    items.push({
      text: (
        <>
          <strong>{t('form.risk.origins')}</strong>{' '}
          {t('form.risk.originsBody', { list: origins.join(', ') })}
        </>
      ),
      link: { label: t('form.after.origins'), to: '#origins' },
    });
  }

  if (form.check_specs !== false) {
    const rules = failureRuleCounts(spec);
    for (const rule of ['reject', 'trash'] as const) {
      if (rules[rule] === 0) continue;
      items.push({
        text: t('form.risk.ruleSet', {
          count: rules[rule],
          rule: t(`fields.ruleOption.${rule}.title`),
        }),
        link: { label: t('form.fields.title'), to: '#fields' },
      });
    }
  }

  const spam = summary?.spam_7d ?? 0;
  if (spam > 0) {
    items.push({
      text: (
        <>
          <strong>{t('form.risk.spam', { count: spam })}</strong> {t('form.risk.spamBody')}
        </>
      ),
      link: {
        label: t('form.risk.review'),
        to: `/account/messages?form=${encodeURIComponent(form.id)}&verdict=spam`,
      },
    });
  }
  return items;
}
